// ============================================================
// หน้า GlReconcile — กระทบยอด GL (GL Reconciliation)
// ------------------------------------------------------------
// เทียบ "ใบแจ้งหนี้ประจำเดือน" (ฝั่งซ้าย) กับ "ใบเสร็จรับเงินบิลน้ำ" (ฝั่งขวา)
// ทีละบ้าน+งวด แล้วบอกว่าตรงกัน / ชำระขาด / ชำระเกิน / ยังไม่ชำระ / ไม่พบใบแจ้งหนี้
//
// ★ หน้านี้ read-only ล้วน — ไม่มีปุ่มบันทึก แก้ไข ลบ หรือตั้งค่าใดๆ
//   ใช้ตรวจก่อนเดินหน้าขั้นชำระเงิน/ปิดงวด ไม่ได้โพสต์รายการเข้าสมุดรายวัน
//
// ★ ตัวเลขทั้งหมดคำนวณที่ backend (report/gl_reconcile_engine.py) เพื่อให้ backend
//   2 ตัว (New-CDS-P1-Backend / CDS_Backend) ให้ผลเหมือนกันเป๊ะ หน้าจอทำแค่แสดงผล
//   กรองฝั่ง client และส่งออก CSV
//
// ★ ตัวเลข 2 ฝั่งต้องเท่ากับหน้ารายงานเดิมเป๊ะ (เงื่อนไขเดียวกัน)
//     ฝั่งซ้าย = "รายงานแจ้งหนี้ประจำเดือน" — รายช่อง + "รวม (บาท)" (item.total_amount)
//     ฝั่งขวา = "รายงานใบเสร็จรับเงิน"      — ใบเสร็จบิลน้ำที่ออกในเดือน รายช่อง + "รวม (บาท)" (item.paid_amount)
//   1 แถว = 1 บ้าน ต่อ 1 เดือน, ผลต่าง = ยอดรับชำระ − ยอดแจ้งหนี้ ของบ้านนั้นในเดือนนั้น
// ============================================================

import React, { Component } from "react";
import Select from "react-select";

import "../../shared/config/global.js";
import "../../assets/css/bootstrap.css";
import Navbar from "../../shared/layout/Navbar";
import {
  tokens,
  GET,
  getStorage,
  format_date,
  toFixed,
  loading,
  permission,
  select_style,
  ScrollTopButton,
  exportCsv,
  csvNumber,
  csvDate,
  csvStamp,
  toBuddhistYear,
} from "../../shared/index.js";

const perm = permission("gl-reconcile");

// ช่องเลือกในแถบตัวกรอง — หน้าตาเดียวกับช่องกรอกหน้าอื่น (พื้นเทา ไม่มีขอบ) ต่างแค่ยกเมนูให้ลอยเหนือการ์ดสรุป
const FILTER_SELECT_STYLE = { ...select_style, menu: (base) => ({ ...base, zIndex: 50 }) };
const FILTER_SELECT_CLASS = "form-control bg-contain border-0 select-search w-100";

// ---------- ค่าคงที่ ----------

const MONTH_OPTIONS = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"].map((label, index) => ({
  value: index + 1,
  label,
}));

// สถานะกระทบยอด — ต้องมีทั้งไอคอนและข้อความ ห้ามสื่อด้วยสีอย่างเดียว (สเปกข้อ 13)
const RECON_STATUS = {
  MATCHED: { label: "ตรงกัน", icon: "●", chip: "bg-success", bar: "#198754" },
  UNDERPAID: { label: "ชำระขาด", icon: "▲", chip: "bg-warning text-dark", bar: "#fd7e14" },
  OVERPAID: { label: "ชำระเกิน", icon: "▲", chip: "bg-primary", bar: "#0d6efd" },
  UNPAID: { label: "ยังไม่ชำระ", icon: "○", chip: "bg-danger", bar: "#dc3545" },
  ORPHAN: { label: "ไม่พบใบแจ้งหนี้", icon: "⚠", chip: "bg-dark", bar: "#212529" },
};

const STATUS_OPTIONS = Object.keys(RECON_STATUS).map((value) => ({ value, label: RECON_STATUS[value].label }));

// การ์ดที่คลิกแล้วกรองตาราง (สเปกข้อ 6)
const QUICK_FILTER = {
  MATCHED: ["MATCHED"],
  OUTSTANDING: ["UNPAID", "UNDERPAID"],
  ABNORMAL: ["OVERPAID", "ORPHAN"],
};

// คอลัมน์ฝั่งซ้าย — ชื่อ/ลำดับตามรายงานแจ้งหนี้ประจำเดือน (Report.js) always = ซ่อนไม่ได้
const LEFT_COLUMNS = [
  { key: "address_number", label: "บ้านเลขที่", align: "left", width: 90, always: true },
  { key: "full_name", label: "ชื่อ – นามสกุล", align: "left", width: 200, always: true },
  { key: "meter_read_date", label: "วันที่จดมาตร", align: "center", width: 100, always: true },
  { key: "meter_current", label: "เลขมาตรจดครั้งนี้", align: "right", width: 90 },
  { key: "meter_previous", label: "เลขมาตรจดครั้งก่อน", align: "right", width: 100 },
  { key: "unit_used", label: "จำนวนหน่วยใช้", align: "right", width: 90, always: true },
  { key: "water_bill", label: "ค่าน้ำประปา", align: "right", width: 90, money: true, always: true },
  { key: "equipment_maintenance_fee", label: "ค่ารักษาอุปกรณ์น้ำ", align: "right", width: 100, money: true },
  { key: "common_expenses", label: "ค่าส่วนกลาง", align: "right", width: 90, money: true },
  { key: "waste_cost", label: "ค่าขยะ", align: "right", width: 80, money: true },
  { key: "cable_cost", label: "ค่าเคเบิล", align: "right", width: 80, money: true },
  // รายงานรวมค่าต่อกลับมาตรไว้ในช่องรายรับอื่นๆ
  { key: "other_income_total", label: "รายรับอื่นๆ", align: "right", width: 90, money: true },
  { key: "fire_cost", label: "ค่าเบี้ยประกัน", align: "right", width: 90, money: true },
  { key: "debt_adjust", label: "เพิ่ม/ลดหนี้", align: "right", width: 90, money: true, signed: true },
  { key: "late_payment", label: "ค่าปรับล่าช้า", align: "right", width: 90, money: true },
];

// คอลัมน์รายช่องของฝั่งขวา — ชื่อ/ลำดับตามรายงานใบเสร็จรับเงิน (Report.js) ซ่อนได้ทั้งหมด
const RIGHT_FIELD_COLUMNS = [
  { key: "water_bill", label: "ค่าน้ำประปา" },
  { key: "equipment_maintenance_fee", label: "ค่ารักษาอุปกรณ์น้ำ" },
  { key: "common_expenses", label: "ค่าส่วนกลาง" },
  { key: "waste_cost", label: "ค่าขยะ" },
  { key: "cable_cost", label: "ค่าเคเบิล" },
  { key: "other_income", label: "รายรับอื่นๆ" },
  { key: "fire_cost", label: "ค่าเบี้ยประกัน" },
  { key: "debt_adjust", label: "เพิ่ม/ลดหนี้", signed: true },
  { key: "meter_connect_cost", label: "ค่าต่อกลับมิเตอร์" },
  { key: "late_payment", label: "ค่าปรับชำระล่าช้า" },
];

// คีย์ของคอลัมน์ฝั่งขวาใน hidden_columns ขึ้นต้นด้วย "r:" กันชนกับคีย์ฝั่งซ้ายที่ชื่อเดียวกัน
const rightKey = (key) => `r:${key}`;

const DEFAULT_HIDDEN = [...LEFT_COLUMNS.filter((column) => !column.always).map((column) => column.key), ...RIGHT_FIELD_COLUMNS.map((column) => rightKey(column.key))];

// คอลัมน์คงที่ของฝั่งขวา (ไม่นับคอลัมน์รายช่อง) — ใช้คำนวณ colSpan
const RIGHT_FIXED_BEFORE = 8; // เลขที่ใบเสร็จ ... สถานะ (ก่อนคอลัมน์รายช่อง)
const RIGHT_FIXED_AFTER = 4; // ยอดรวมของบิล, ผลรวมยอดชำระตามบิล, สถานะกระทบยอด, ยอดปัดเศษ (ไม่มีคอลัมน์ผลต่างแล้ว)

// มุมมองคอลัมน์: SUMMARY = เฉพาะคอลัมน์ยอด (ซ่อนรายช่อง) · DETAIL = เปิดรายช่องทั้งหมด
const OPTIONAL_KEYS = DEFAULT_HIDDEN;

// แถวที่อีกฝั่งไม่มีคู่ (UNPAID/ORPHAN) — สร้างรายการเปล่าให้ทุกช่องแสดง 0.00 แทนช่องว่าง
const ghostLeftItem = (row) => ({ ghost: true, address_number: row.address_number, full_name: row.right?.[0]?.full_name || "", placeholder: "ไม่พบใบแจ้งหนี้" });
const ghostRightItem = (row) => ({ ghost: true, address_number: row.address_number, full_name: row.left?.[0]?.full_name || "", placeholder: "ไม่พบใบเสร็จ" });

// ความสูงของ 1 บรรทัดในตาราง — ซ้าย-ขวาต้องใช้ค่าเดียวกัน ไม่งั้นแถวเหลื่อม
const LINE_HEIGHT = 38;
// เกินจำนวนนี้จึงเริ่ม virtual scroll (สเปกข้อ 13)
const VIRTUAL_THRESHOLD = 200;

// ---------- ตัวช่วยแสดงผล ----------

const money = (value) => toFixed(Number(value) || 0);
const int = (value) => String(Math.round(Number(value) || 0));
const thaiDate = (value) => (value ? format_date(value, "dd/mm/yyyy") : "");
// วันที่ที่อาจไม่มีค่า (เช่น ใบเสร็จที่ไม่มีบิลน้ำ) — แสดง "-" ตามสไตล์ไฟล์
const thaiDateOrDash = (value) => (value ? thaiDate(value) : "-");

// งวด YYYY-MM (ค.ศ.) -> MM/พ.ศ.
const periodTH = (period) => {
  if (!period) return "";
  const [year, month] = String(period).split("-");
  return `${month}/${toBuddhistYear(year)}`;
};

// ตัวเลขติดลบให้เป็นสีแดงในวงเล็บ (Edge case #10)
const signedMoney = (value) => {
  const amount = Number(value) || 0;
  if (amount < 0) return <span className="text-danger">({money(Math.abs(amount))})</span>;
  return money(amount);
};

// segmented control (tab toggle) — ใช้ bootstrap tokens เดิม ไม่เพิ่ม library
const SEG_CSS = `
.gl-seg{display:inline-flex;max-width:100%;padding:2px;border:1px solid #ced4da;border-radius:8px;background:#f1f3f5}
.gl-seg.gl-seg-block{display:flex;width:100%}
.gl-seg-btn{flex:1 1 auto;border:0;background:transparent;color:#495057;border-radius:6px;padding:0 14px;font-size:inherit;line-height:1.2;white-space:nowrap;cursor:pointer;transition:background .15s,color .15s}
.gl-seg-btn.gl-sm{font-size:.875rem;padding:0 12px}
.gl-seg-btn:hover:not(:disabled):not(.active){background:#e2e6ea;color:#212529}
.gl-seg-btn.active{background:#007bff;color:#fff;font-weight:600;box-shadow:0 1px 2px rgba(0,0,0,.2)}
.gl-seg-btn:focus-visible{outline:2px solid #0056b3;outline-offset:1px}
.gl-seg-btn:disabled{opacity:.5;cursor:not-allowed}
`;
const SegTabs = ({ label, value, options, onChange, height, small, block }) => {
  const enabled = options.filter((o) => !o.disabled);
  const onKeyDown = (event, option) => {
    const i = enabled.indexOf(option);
    let next = null;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") next = enabled[(i + 1) % enabled.length];
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") next = enabled[(i - 1 + enabled.length) % enabled.length];
    if (!next) return;
    event.preventDefault();
    onChange(next.value);
    const buttons = event.currentTarget.parentNode.querySelectorAll("[role=tab]:not(:disabled)");
    buttons[enabled.indexOf(next)]?.focus();
  };
  return (
    <div className={`gl-seg${block ? " gl-seg-block" : ""}`} role="tablist" aria-label={label}>
      <style>{SEG_CSS}</style>
      {options.map((option) => {
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected || (!enabled.some((o) => o.value === value) && option === enabled[0]) ? 0 : -1}
            disabled={option.disabled}
            title={option.disabled ? option.title : undefined}
            className={`gl-seg-btn${small ? " gl-sm" : ""}${selected ? " active" : ""}`}
            style={{ height: height - 6 }}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => onKeyDown(event, option)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

export default class GlReconcile extends Component {
  constructor(props) {
    super(props);

    const now = new Date();

    this.state = {
      // ---- ตัวกรอง (ค่าที่กำลังแก้ในแถบด้านบน) ----
      mode: "MONTH",
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      address_number: null,
      status: null,
      only_diff: false,

      // ---- ข้อมูล ----
      data_customer: [],
      summary: null,
      rows: null, // null = ยังไม่เคยค้นหา (Initial state)
      rows_mode: null, // โหมดของชุดข้อมูลที่ถืออยู่จริง (คนละตัวกับ mode ในตัวกรอง)
      meta: null,
      searching: false,
      error: "",

      // ---- การแสดงผล ----
      quick: null, // การ์ดที่ถูกคลิกอยู่
      hidden_columns: DEFAULT_HIDDEN,
      show_columns_menu: false,
      show_export_menu: false,
      selected_key: null,
      hover_key: null,
      view: "SPLIT", // SPLIT = เทียบ 2 ฝั่ง, LEFT = ใบแจ้งหนี้อย่างเดียว, RIGHT = ใบเสร็จอย่างเดียว
      fullscreen: false, // ดูตารางแบบเต็มจอ
      scroll_top: 0,
      viewport_height: 600,
    };

    this.leftScroll = React.createRef();
    this.rightScroll = React.createRef();
    this.tablesRef = React.createRef(); // กรอบตาราง — ตัวที่ขยายเต็มจอ
    this.syncing = false;
  }

  componentDidMount() {
    loading(false);
    if (!perm.view) return;
    window.addEventListener("keydown", this.onKeyDown);
    document.addEventListener("fullscreenchange", this.onFullscreenChange);
    this.GetCustomerAll();
    this.Search();
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.onKeyDown);
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
  }

  // ---------- เต็มจอ ----------
  // ใช้ Fullscreen API ของเบราว์เซอร์กับกรอบตาราง (ซ่อนแถบเบราว์เซอร์ด้วย)
  // เบราว์เซอร์ที่ไม่รองรับ (เช่น iPhone) ยังได้กรอบเต็มหน้าต่างจาก style position: fixed
  toggleFullscreen = () => {
    if (this.state.fullscreen) {
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
      this.setState({ fullscreen: false }, this.resetScroll);
      return;
    }
    this.setState({ fullscreen: true }, () => {
      this.tablesRef.current?.requestFullscreen?.().catch(() => {});
      this.resetScroll();
    });
  };

  // สลับมุมมอง/เต็มจอแล้วกล่องเลื่อนเปลี่ยนขนาด — กลับไปบนสุดและคิดช่วงของ virtual scroll ใหม่
  // ไม่งั้นตารางที่เกิน VIRTUAL_THRESHOLD แถวจะวาดช่วงแถวผิดจนเห็นเป็นที่ว่าง
  resetScroll = () => {
    const pane = this.leftScroll.current || this.rightScroll.current;
    [this.leftScroll.current, this.rightScroll.current].forEach((el) => el && (el.scrollTop = 0));
    this.setState({ scroll_top: 0, viewport_height: pane?.clientHeight || 600 });
  };

  // กด Esc ออกจากเต็มจอของเบราว์เซอร์ -> sync state ตาม
  onFullscreenChange = () => {
    if (!document.fullscreenElement && this.state.fullscreen) this.setState({ fullscreen: false }, this.resetScroll);
  };

  // กด Esc ออกจากเต็มจอ — ปกติเบราว์เซอร์จัดการเองแล้วส่ง fullscreenchange มา
  // แต่โหมดกรอบเต็มหน้าต่าง (เบราว์เซอร์ไม่รองรับ Fullscreen API) และบางเบราว์เซอร์ส่ง keydown มาให้หน้าเว็บ ต้องปิดเอง
  onKeyDown = (event) => {
    if (event.key === "Escape" && this.state.fullscreen) this.toggleFullscreen();
  };

  // มุมมองที่ใช้จริง — จอแคบเทียบ 2 ฝั่งไม่พอ ให้เหลือทีละตาราง
  effectiveView = () => this.state.view;

  // ---------- โหลดข้อมูล ----------

  GetCustomerAll = async () => {
    const result = await GET(tokens, `v1/TH/customer-manage/filter-search/page/0/${getStorage("project_id")}`, { data_search: "", transfer_date: "", status_transfer: "" });
    if (result?.status) {
      this.setState({ data_customer: result.data.data.map((item) => ({ value: item.address_number, label: `อาคาร ${item.building} ห้อง ${item.address_number}` })) });
    }
  };

  Search = async () => {
    const s = this.state;
    this.setState({ searching: true, error: "", quick: null, selected_key: null, scroll_top: 0 });
    loading(true);

    const result = await GET(tokens, `v1/TH/report/gl-reconcile/filter-search/${getStorage("project_id")}`, {
      mode: s.mode,
      year: s.year,
      month: s.month,
      address_number: s.address_number?.value || "",
      status_transfer: "",
    });

    loading(false);

    // ★ ฝั่งใดฝั่งหนึ่งพังต้องไม่แสดงตัวเลขบางส่วน เพราะจะอ่านผลกระทบยอดผิด (สเปกข้อ 3.3)
    if (!result?.status) {
      this.setState({ searching: false, error: "โหลดข้อมูลกระทบยอดไม่สำเร็จ", summary: null, rows: null, rows_mode: null, meta: null });
      return;
    }

    this.setState({
      searching: false,
      summary: result.data.summary,
      rows: result.data.rows || [],
      rows_mode: s.mode,
      meta: result.data.meta,
    });
  };

  Reset = () => {
    const now = new Date();
    this.setState(
      {
        mode: "MONTH",
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        address_number: null,
        status: null,
        only_diff: false,
        quick: null,
        hidden_columns: DEFAULT_HIDDEN,
        summary: null,
        rows: null,
        rows_mode: null,
        meta: null,
        error: "",
        selected_key: null,
      },
      () => {
        if (this.leftScroll.current) this.leftScroll.current.scrollTop = 0;
        if (this.rightScroll.current) this.rightScroll.current.scrollTop = 0;
      }
    );
  };

  // เปลี่ยนโหมดรายเดือน/รายปีแล้วรีเฟรชทันที (สเปกข้อ 5)
  ChangeMode = (mode) => {
    if (mode === this.state.mode) return;
    this.setState({ mode, rows: null, rows_mode: null, summary: null, meta: null }, this.Search);
  };

  // คลิกแถวเดือนในโหมดรายปี -> เข้าโหมดรายเดือนของเดือนนั้น (AC-10)
  DrillDown = (month) => {
    this.setState({ mode: "MONTH", month, rows: null, rows_mode: null, summary: null, meta: null }, this.Search);
  };

  // ---------- กรองฝั่ง client ----------

  visibleRows = () => {
    const s = this.state;
    let rows = s.rows || [];
    if (s.rows_mode === "YEAR") return rows;

    // ซ่อนใบเสร็จที่ยกเลิกทั้งหมด (จอ + ไฟล์ export ใช้ชุดเดียวกัน) — ยอดรวมมาจาก backend ซึ่งไม่นับใบยกเลิกอยู่แล้ว
    // แถวที่มีแต่ใบยกเลิกและไม่มีใบแจ้งหนี้คู่ (VOID) ตัดทิ้งทั้งแถว
    rows = rows
      .filter((row) => row.status !== "VOID")
      .map((row) => (row.right?.some((item) => item.is_void) ? { ...row, right: row.right.filter((item) => !item.is_void) } : row));

    if (s.quick) rows = rows.filter((row) => QUICK_FILTER[s.quick].includes(row.status));
    if (s.status) rows = rows.filter((row) => row.status === s.status.value);
    if (s.only_diff) rows = rows.filter((row) => row.status !== "MATCHED");
    return rows;
  };

  toggleQuick = (name) => this.setState({ quick: this.state.quick === name ? null : name, selected_key: null });

  // สลับมุมมองคอลัมน์ สรุป (ซ่อนรายช่อง) / รายละเอียด (เปิดรายช่องทั้งหมด)
  columnPreset = () => {
    const hidden = this.state.hidden_columns;
    if (OPTIONAL_KEYS.every((key) => hidden.includes(key))) return "SUMMARY";
    if (OPTIONAL_KEYS.every((key) => !hidden.includes(key))) return "DETAIL";
    return "CUSTOM";
  };

  setColumnPreset = (preset) => this.setState({ hidden_columns: preset === "DETAIL" ? [] : DEFAULT_HIDDEN });

  toggleColumn = (key) => {
    const hidden = this.state.hidden_columns;
    this.setState({ hidden_columns: hidden.includes(key) ? hidden.filter((k) => k !== key) : [...hidden, key] });
  };

  // ---------- Sync scroll (AC-09) ----------

  onScroll = (source) => (event) => {
    if (this.syncing) return;
    this.syncing = true;

    const top = event.target.scrollTop;
    const other = source === "left" ? this.rightScroll.current : this.leftScroll.current;
    if (other && other.scrollTop !== top) other.scrollTop = top;

    // อัปเดต window ของ virtual scroll จากฝั่งเดียวพอ (ความสูงแถว 2 ฝั่งเท่ากันเสมอ)
    // ข้อมูลน้อยกว่าเกณฑ์ไม่ต้อง setState ทุก event เพราะวาดครบทุกแถวอยู่แล้ว
    if ((this.state.rows || []).length > VIRTUAL_THRESHOLD) {
      this.setState({ scroll_top: top, viewport_height: event.target.clientHeight || 600 });
    }
    this.syncing = false;
  };

  // ---------- ส่งออก CSV ----------

  fileName = (kind) => {
    const s = this.state;
    const scope = s.mode === "YEAR" ? `รายปี_${toBuddhistYear(s.year)}` : `รายเดือน_${String(s.month).padStart(2, "0")}${toBuddhistYear(s.year)}`;
    return `GL-Reconcile_${kind}_${scope}_${csvStamp()}.csv`;
  };

  // แบบที่ 2 — รายละเอียดใบแจ้งหนี้ (ทุกคอลัมน์ ไม่ว่าจะซ่อนไว้หรือไม่) = ตารางรายงานแจ้งหนี้ประจำเดือน
  exportInvoiceCsv = () => {
    const rows = this.visibleRows();
    const headers = [...LEFT_COLUMNS.map((column) => column.label), "รวม (บาท)", "เดือน", "สถานะกระทบยอด"];
    const body = [];
    const totals = {};

    for (const row of rows) {
      // ห้องที่ไม่มีใบแจ้งหนี้ออกเป็นบรรทัดค่า 0 (ไม่ข้ามแถว)
      for (const item of row.left.length ? row.left : [ghostLeftItem(row)]) {
        body.push([
          ...LEFT_COLUMNS.map((column) => {
            const value = item[column.key];
            if (item.ghost && column.key === "full_name") return value || item.placeholder;
            if (item.ghost && column.key === "meter_read_date") return "-";
            if (column.key === "meter_read_date") return csvDate(value);
            if (column.money) {
              totals[column.key] = (totals[column.key] || 0) + (Number(value) || 0);
              return csvNumber(value);
            }
            if (column.key === "address_number" || column.key === "full_name") return value || "";
            totals[column.key] = (totals[column.key] || 0) + (Number(value) || 0);
            return int(value);
          }),
          csvNumber(item.total_amount),
          periodTH(row.bill_period),
          RECON_STATUS[row.status]?.label || row.status,
        ]);
      }
    }

    body.push([
      ...LEFT_COLUMNS.map((column) => {
        if (column.key === "address_number") return "รวมทั้งสิ้น";
        if (column.key === "full_name" || column.key === "meter_read_date") return "";
        if (column.key === "meter_current" || column.key === "meter_previous") return "";
        return column.money ? csvNumber(totals[column.key]) : int(totals[column.key]);
      }),
      csvNumber(sum(rows, "billed_amount")),
      "",
      "",
    ]);

    exportCsv(this.fileName("รายละเอียดใบแจ้งหนี้"), headers, body);
  };

  // แบบที่ 3 — รายละเอียดใบเสร็จ (ทุกคอลัมน์รายช่อง) = ตารางรายงานใบเสร็จรับเงิน
  // ใบที่ยกเลิกถูกกรองออกใน visibleRows แล้ว ไฟล์จึงตรงกับที่เห็นบนจอ
  exportReceiptCsv = () => {
    const rows = this.visibleRows();
    const headers = ["เลขที่ใบเสร็จ", "วันที่ใบเสร็จ", "วันที่จดมาตร", "บ้านเลขที่", "ชื่อ-นามสกุล", "ประเภทบิล", "ช่องทางชำระ", "สถานะ", ...RIGHT_FIELD_COLUMNS.map((column) => column.label), "ยอดรวมของบิล", "ผลรวมยอดชำระตามบิล", "ยอดสุทธิบนใบเสร็จ", "สถานะกระทบยอด", "ยอดปัดเศษ"];
    const body = [];
    const totals = {};

    for (const row of rows) {
      // ห้องที่ไม่มีใบเสร็จออกเป็นบรรทัดค่า 0 (ไม่ข้ามแถว)
      for (const item of row.right.length ? row.right : [ghostRightItem(row)]) {
        for (const column of RIGHT_FIELD_COLUMNS) totals[column.key] = (totals[column.key] || 0) + (Number(item[column.key]) || 0);
        totals.bill_amount = (totals.bill_amount || 0) + (Number(item.bill_amount) || 0);
        totals.receipt_total_final = (totals.receipt_total_final || 0) + (Number(item.receipt_total_final) || 0);
        body.push([
          item.ghost ? item.placeholder : item.iw_number || "",
          item.ghost ? "-" : csvDate(item.record_date),
          item.meter_read_date ? csvDate(item.meter_read_date) : "-",
          item.address_number || "",
          item.full_name || "",
          item.ghost ? "-" : "บิลน้ำ",
          item.payment_type_label || (item.ghost ? "-" : ""),
          item.ghost ? "-" : "ปกติ",
          ...RIGHT_FIELD_COLUMNS.map((column) => csvNumber(item[column.key])),
          csvNumber(item.bill_amount),
          csvNumber(item.receipt_paid_total),
          csvNumber(item.receipt_total_final),
          RECON_STATUS[row.status]?.label || row.status,
          csvNumber(item.rounding_amount),
        ]);
      }
    }

    // ผลรวมยอดชำระตามบิล = ยอดทั้งใบซ้ำในทุก slice ของใบเดียวกัน จึงไม่รวมท้ายไฟล์ (กันนับซ้ำ)
    body.push(["รวมทั้งสิ้น", "", "", "", "", "", "", "", ...RIGHT_FIELD_COLUMNS.map((column) => csvNumber(totals[column.key])), csvNumber(totals.bill_amount), "", csvNumber(totals.receipt_total_final), "", csvNumber(sumRoundingAmount(rows))]);
    exportCsv(this.fileName("รายละเอียดใบเสร็จ"), headers, body);
  };

  // แบบที่ 4 — Excel กระทบยอด (.xlsx) ตามแบบที่ลูกค้าวาด (GL.xlsx) · 1 แถว = 1 ใบเสร็จ · โครงอยู่ใน glExcel.js
  exportGlExcel = async () => {
    const s = this.state;
    const { downloadGlExcel } = await import("./glExcel.js");
    const filters = [
      `งวด ${String(s.month).padStart(2, "0")}/${toBuddhistYear(s.year)}`,
      s.address_number ? `บ้านเลขที่ ${s.address_number.label || s.address_number.value || s.address_number}` : null,
      s.status ? `สถานะ ${s.status.label}` : null,
      s.quick ? `การ์ดที่กรอง: ${{ MATCHED: "ตรงกัน", OUTSTANDING: "ค้างชำระ", ABNORMAL: "รายการผิดปกติ" }[s.quick]}` : null,
      s.only_diff ? "เฉพาะรายการที่ไม่ตรงกัน" : null,
    ].filter(Boolean);
    downloadGlExcel(this.fileName("กระทบยอด").replace(/\.csv$/, ".xlsx"), {
      rows: this.visibleRows(),
      summary: s.summary,
      periodLabel: periodTH,
      scopeText: filters.join(" · "),
    });
  };

  // ---------- Virtual scroll ----------

  // ความสูงของแถวเปรียบเทียบ 1 แถว — ซ้าย-ขวาใช้ค่าเดียวกันเสมอ แถวจึงไม่เหลื่อม
  rowHeight = (row) => Math.max(1, row.left?.length || 0, row.right?.length || 0) * LINE_HEIGHT;

  // คำนวณช่วงแถวที่ต้องวาดจริง + ความสูง spacer หัว/ท้าย
  windowRows = (rows) => {
    const heights = rows.map(this.rowHeight);
    const total = heights.reduce((a, b) => a + b, 0);

    if (rows.length <= VIRTUAL_THRESHOLD) return { start: 0, end: rows.length, padTop: 0, padBottom: 0, total };

    const { scroll_top, viewport_height } = this.state;
    const overscan = 400; // วาดเผื่อบน-ล่างกันขอบขาวตอนเลื่อนเร็ว

    let offset = 0;
    let start = 0;
    while (start < rows.length && offset + heights[start] < scroll_top - overscan) {
      offset += heights[start];
      start += 1;
    }
    const padTop = offset;

    let end = start;
    let drawn = 0;
    const need = viewport_height + overscan * 2;
    while (end < rows.length && drawn < need) {
      drawn += heights[end];
      end += 1;
    }

    return { start, end, padTop, padBottom: total - padTop - drawn, total };
  };

  // ---------- render: แถบตัวกรอง ----------

  renderFilterBar() {
    const s = this.state;
    const hasRows = !!(s.rows && s.rows.length);

    const yearOptions = [];
    for (let year = new Date().getFullYear() + 1; year >= 2018; year -= 1) {
      yearOptions.push({ value: year, label: String(toBuddhistYear(year)) });
    }

    // ป้ายชื่อช่องกรอก — วางบนช่องเสมอ ความสูงเท่ากันทุกช่องเพื่อให้แถวล่างตรงแนวกัน
    const fieldLabel = (text) => (
      <label className="d-block mb-1 text-muted text-nowrap" style={{ fontSize: 13 }}>
        {text}
      </label>
    );

    return (
      <div className="position-sticky bg-white pt-2 pb-2" style={{ top: 0, zIndex: 20 }}>
        <div className="border rounded-3 p-3 mb-3 bg-white">
          <div className="d-flex flex-wrap align-items-end justify-content-between">
            <div className="d-flex flex-wrap align-items-end">
              <div className="mr-3 mb-2" style={{ width: 200 }}>
                {fieldLabel("มุมมอง")}
                <SegTabs
                  label="มุมมองรายเดือน/รายปี"
                  value={s.mode}
                  height={44}
                  block
                  options={[
                    { value: "MONTH", label: "รายเดือน" },
                    { value: "YEAR", label: "รายปี" },
                  ]}
                  onChange={this.ChangeMode}
                />
              </div>

              <div className="mr-3 mb-2" style={{ width: 130 }}>
                {fieldLabel("ปี (พ.ศ.)")}
                <Select className={FILTER_SELECT_CLASS} styles={FILTER_SELECT_STYLE} options={yearOptions} value={yearOptions.find((option) => option.value === s.year) || null} onChange={(option) => this.setState({ year: option?.value || new Date().getFullYear() })} />
              </div>

              {s.mode === "MONTH" && (
                <div className="mr-3 mb-2" style={{ width: 160 }}>
                  {fieldLabel("เดือน")}
                  <Select className={FILTER_SELECT_CLASS} styles={FILTER_SELECT_STYLE} options={MONTH_OPTIONS} value={MONTH_OPTIONS.find((option) => option.value === s.month) || null} onChange={(option) => this.setState({ month: option?.value || 1 })} />
                </div>
              )}

              <div className="mr-3 mb-2" style={{ width: 240 }}>
                {fieldLabel("บ้านเลขที่")}
                <Select className={FILTER_SELECT_CLASS} styles={FILTER_SELECT_STYLE} options={s.data_customer} value={s.address_number} isClearable placeholder="ทั้งหมด" onChange={(option) => this.setState({ address_number: option })} />
              </div>

              {s.mode === "MONTH" && (
                <div className="mr-3 mb-2" style={{ width: 190 }}>
                  {fieldLabel("สถานะกระทบยอด")}
                  <Select className={FILTER_SELECT_CLASS} styles={FILTER_SELECT_STYLE} options={STATUS_OPTIONS} value={s.status} isClearable placeholder="ทั้งหมด" onChange={(option) => this.setState({ status: option, quick: null })} />
                </div>
              )}

              <div className="mb-2 d-flex">
                <button className="btn btn-primary px-4" style={{ height: 44 }} onClick={this.Search} disabled={s.searching}>
                  ค้นหา
                </button>
                <button className="btn btn-secondary px-3 ml-2" style={{ height: 44 }} onClick={this.Reset} disabled={s.searching}>
                  รีเซ็ต
                </button>
              </div>
            </div>

            <div className="d-flex mb-2">
              {s.mode === "MONTH" && (
                <div className="position-relative mr-2">
                  <button className="btn btn-outline-dark px-3" style={{ height: 44 }} onClick={() => this.setState({ show_columns_menu: !s.show_columns_menu, show_export_menu: false })}>
                    คอลัมน์ ▾
                  </button>
                  {s.show_columns_menu && (
                    // start-0 (ไม่ใช้ end-0) กันเมนูยื่นไปทางซ้ายจนโดนเมนูด้านข้างบัง — ปุ่มนี้มักหลุดไปอยู่ชิดซ้ายเวลาแถบตัวกรองตัดบรรทัด
                    <div className="position-absolute bg-white border rounded shadow p-2 start-0" style={{ zIndex: 40, minWidth: 220, maxHeight: 420, overflowY: "auto" }}>
                      <div className="text-muted fw-bold py-1" style={{ fontSize: 12 }}>
                        ใบแจ้งหนี้ (ซ้าย)
                      </div>
                      {LEFT_COLUMNS.filter((column) => !column.always).map((column) => (
                        <label key={column.key} className="d-flex align-items-center py-1 mb-0 text-nowrap" style={{ cursor: "pointer" }}>
                          <input type="checkbox" className="form-check-input mt-0 mr-2" checked={!s.hidden_columns.includes(column.key)} onChange={() => this.toggleColumn(column.key)} />
                          {column.label}
                        </label>
                      ))}
                      <div className="text-muted fw-bold py-1 mt-2 border-top" style={{ fontSize: 12 }}>
                        ใบเสร็จ (ขวา)
                      </div>
                      {RIGHT_FIELD_COLUMNS.map((column) => (
                        <label key={rightKey(column.key)} className="d-flex align-items-center py-1 mb-0 text-nowrap" style={{ cursor: "pointer" }}>
                          <input type="checkbox" className="form-check-input mt-0 mr-2" checked={!s.hidden_columns.includes(rightKey(column.key))} onChange={() => this.toggleColumn(rightKey(column.key))} />
                          {column.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {s.rows_mode === "MONTH" && (
                <div className="position-relative">
                  <button className="btn btn-dark px-3" style={{ height: 44 }} disabled={!hasRows} onClick={() => this.setState({ show_export_menu: !s.show_export_menu, show_columns_menu: false })}>
                    Export ▾
                  </button>
                  {s.show_export_menu && hasRows && (
                    <div className="position-absolute bg-white border rounded shadow start-0" style={{ zIndex: 40, minWidth: 240 }}>
                      <button className="btn btn-link text-decoration-none text-dark w-100 text-left px-3 py-2 border-bottom" onClick={() => this.runExport(this.exportGlExcel)}>
                        ส่งออก Excel (กระทบยอด)
                      </button>
                      <button className="btn btn-link text-decoration-none text-dark w-100 text-left px-3 py-2" onClick={() => this.runExport(this.exportInvoiceCsv)}>
                        รายละเอียดใบแจ้งหนี้
                      </button>
                      <button className="btn btn-link text-decoration-none text-dark w-100 text-left px-3 py-2" onClick={() => this.runExport(this.exportReceiptCsv)}>
                        รายละเอียดใบเสร็จ
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {s.mode === "MONTH" && (
            <div className="border-top pt-3 mt-1">
              <div className="d-flex flex-wrap align-items-center">
                <label className="d-flex align-items-center mb-0 mr-4 text-nowrap" style={{ cursor: "pointer" }}>
                  <input type="checkbox" className="form-check-input mt-0 mr-2" checked={s.only_diff} onChange={() => this.setState({ only_diff: !s.only_diff })} />
                  แสดงเฉพาะรายการที่ไม่ตรงกัน
                </label>
              </div>
              <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                ฝั่งซ้าย = รายงานแจ้งหนี้ประจำเดือน, ฝั่งขวา = รายงานใบเสร็จรับเงิน (ใบเสร็จบิลน้ำที่ออกในเดือน) — ผลต่าง = ยอดรับชำระ − ยอดแจ้งหนี้ ของบ้านนั้นในเดือนนั้น
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  runExport = (fn) => {
    this.setState({ show_export_menu: false });
    fn();
  };

  // ---------- render: การ์ดสรุป ----------

  renderCards() {
    const s = this.state;
    const d = s.summary;

    // การ์ดสรุป — ใบที่คลิกได้ต้องดูออกว่าคลิกได้ (ขอบ + เคอร์เซอร์ + ป้ายบอกสถานะกรอง)
    const card = (title, main, sub, extra) => {
      const clickable = !!extra?.onClick;
      const active = !!extra?.active;
      return (
        <div
          className={`card p-3 ${active ? "border-primary" : "border"} ${clickable ? "shadow-sm" : ""}`}
          style={{
            flex: "1 1 200px",
            minWidth: 200,
            cursor: clickable ? "pointer" : "default",
            borderWidth: 2,
            borderColor: active ? "#0d6efd" : "#e6e8eb",
            backgroundColor: active ? "#f2f7ff" : "#fff",
          }}
          onClick={extra?.onClick}
          title={clickable ? "คลิกเพื่อกรองเฉพาะกลุ่มนี้" : undefined}
        >
          <div className="d-flex align-items-center justify-content-between text-muted" style={{ fontSize: 13 }}>
            <span>{title}</span>
            {clickable && <span style={{ fontSize: 11 }}>{active ? "กรองอยู่ ✕" : "กรอง"}</span>}
          </div>
          <div className={`fw-bold ${extra?.tone || ""}`} style={{ fontSize: 24, lineHeight: 1.3 }}>
            {main}
          </div>
          <div className="text-muted" style={{ fontSize: 12 }}>
            {sub}
          </div>
        </div>
      );
    };

    if (!d) {
      return <div className="d-flex flex-wrap gap-2 mb-3">{["ยอดแจ้งหนี้รวม", "ยอดรับชำระรวม", "ผลต่าง", "ตรงกัน", "ค้างชำระ", "รายการผิดปกติ"].map((title) => <React.Fragment key={title}>{card(title, "–", "–")}</React.Fragment>)}</div>;
    }

    const variance = Number(d.variance) || 0;
    const varianceTone = Math.abs(variance) < 0.01 ? "text-success" : variance < 0 ? "text-danger" : "text-warning";

    return (
      <>
        <div className="d-flex flex-wrap gap-2 mb-3">
          {card("ยอดแจ้งหนี้รวม", money(d.billed_amount), `${d.billed_count} ใบ (รายงานแจ้งหนี้ประจำเดือน)`)}
          {card("ยอดรับชำระรวม", money(d.paid_amount), `${d.paid_count} ใบ (รายงานใบเสร็จรับเงิน)`)}
          {card("ผลต่าง", money(variance), d.variance_percent === null ? "–" : `${money(d.variance_percent)}%`, { tone: varianceTone })}
          {card("ตรงกัน", `${d.matched_count} / ${d.row_count}`, d.matched_percent === null ? "–" : `${money(d.matched_percent)}%`, {
            onClick: () => this.toggleQuick("MATCHED"),
            active: s.quick === "MATCHED",
          })}
          {card("ค้างชำระ", `${d.unpaid_count} ราย`, money(d.unpaid_amount), {
            onClick: () => this.toggleQuick("OUTSTANDING"),
            active: s.quick === "OUTSTANDING",
          })}
          {card(
            <>
              รายการผิดปกติ {d.abnormal_count > 0 && <span className="badge bg-danger ml-1">{d.abnormal_count}</span>}
            </>,
            `${d.abnormal_count} ราย`,
            `ชำระเกิน ${d.overpaid_count} / ไม่พบใบแจ้งหนี้ ${d.orphan_receipt_count}`,
            { onClick: () => this.toggleQuick("ABNORMAL"), active: s.quick === "ABNORMAL" }
          )}
        </div>
      </>
    );
  }

  // ---------- render: แถบเตือน ----------

  renderBanners() {
    const { meta, summary } = this.state;
    if (!meta) return null;

    const warnings = [];
    if (summary?.orphan_receipt_count > 0) warnings.push(`พบใบเสร็จ ${summary.orphan_receipt_count} รายการที่ไม่มีใบแจ้งหนี้คู่กัน`);
    if (meta.bill_without_date_count > 0) warnings.push(`มีใบแจ้งหนี้ ${meta.bill_without_date_count} ใบที่ไม่มีวันที่ จึงไม่ถูกนับในทุกช่วงวันที่`);
    if (meta.non_water_receipt_count > 0) warnings.push(`ตัดใบเสร็จที่ไม่ใช่บิลน้ำออก ${meta.non_water_receipt_count} ใบ (รายงานใบเสร็จรับเงินก็ไม่นับ)`);
    if (meta.receipt_total_mismatch_count > 0)
      warnings.push(`มีใบเสร็จ ${meta.receipt_total_mismatch_count} ใบที่ยอดรวมรายช่องไม่เท่ากับยอดสุทธิที่บันทึกบนใบเสร็จ (หน้าประวัติค่าใช้จ่ายแสดงยอดสุทธิ) — หน้านี้ยึดยอดตามรายงานใบเสร็จรับเงิน ดูใบที่มีเครื่องหมาย ⓘ`);

    if (!warnings.length) return null;

    return (
      <div className="alert alert-warning py-2 px-3 mb-3" style={{ fontSize: 13 }}>
        {warnings.map((text) => (
          <div key={text}>⚠ {text}</div>
        ))}
      </div>
    );
  }

  // ---------- render: ตารางโหมดรายเดือน ----------

  leftColumns = () => LEFT_COLUMNS.filter((column) => !this.state.hidden_columns.includes(column.key));

  rightFieldColumns = () => RIGHT_FIELD_COLUMNS.filter((column) => !this.state.hidden_columns.includes(rightKey(column.key)));

  renderLeftHead() {
    return (
      <thead className="position-sticky bg-white" style={{ top: 0, zIndex: 2 }}>
        <tr>
          {this.leftColumns().map((column) => (
            <th key={column.key} className={`text-${column.align}`} style={{ minWidth: column.width }}>
              {column.label}
            </th>
          ))}
          <th className="text-right" style={{ minWidth: 110 }}>
            รวม (บาท)
          </th>
        </tr>
      </thead>
    );
  }

  renderLeftRow(row) {
    const columns = this.leftColumns();
    const height = this.rowHeight(row);

    // ฝั่งนี้ไม่มีคู่ -> ใช้รายการเปล่า ทุกช่องเป็น 0.00 สูง 1 บรรทัดเท่าเดิม แถวไม่เหลื่อม
    const items = row.left.length ? row.left : [ghostLeftItem(row)];
    const isGhost = !row.left.length;

    return (
      <tr key={row.key} style={{ height, borderLeft: `3px solid ${RECON_STATUS[row.status]?.bar || "transparent"}` }} className={`${isGhost ? "text-muted " : ""}${this.rowClass(row)}`} onMouseEnter={() => this.hoverRow(row.key)} onClick={() => this.setState({ selected_key: row.key })}>
        {columns.map((column) => (
          <td key={column.key} className={`text-${column.align} align-top`}>
            {items.map((item, index) => (
              <div key={index} style={{ height: LINE_HEIGHT, lineHeight: `${LINE_HEIGHT}px` }} className="text-truncate" title={column.key === "full_name" ? item.full_name : undefined}>
                {this.leftCell(column, item)}
              </div>
            ))}
          </td>
        ))}
        <td className="text-right align-top fw-bold">
          {items.map((item, index) => (
            <div key={index} style={{ height: LINE_HEIGHT, lineHeight: `${LINE_HEIGHT}px` }}>
              {money(item.total_amount)}
            </div>
          ))}
        </td>
      </tr>
    );
  }

  leftCell(column, item) {
    const value = item[column.key];
    if (item.ghost && column.key === "full_name") return value || <span className="fst-italic">{item.placeholder}</span>;
    if (item.ghost && column.key === "meter_read_date") return "-";
    if (column.key === "meter_read_date") return thaiDate(value);
    if (column.signed) return signedMoney(value);
    if (column.money) return money(value);
    if (column.key === "address_number" || column.key === "full_name") return value || "";
    return int(value);
  }

  renderRightHead() {
    return (
      <thead className="position-sticky bg-white" style={{ top: 0, zIndex: 2 }}>
        <tr>
          <th style={{ minWidth: 140 }}>เลขที่ใบเสร็จ</th>
          <th className="text-center" style={{ minWidth: 100 }}>
            วันที่ใบเสร็จ
          </th>
          <th className="text-center" style={{ minWidth: 100 }}>
            วันที่จดมาตร
          </th>
          <th style={{ minWidth: 90 }}>บ้านเลขที่</th>
          <th style={{ minWidth: 180 }}>ชื่อ – นามสกุล</th>
          <th className="text-center" style={{ minWidth: 80 }}>
            ประเภทบิล
          </th>
          <th className="text-center" style={{ minWidth: 90 }}>
            ช่องทางชำระ
          </th>
          <th className="text-center" style={{ minWidth: 80 }}>
            สถานะ
          </th>
          {this.rightFieldColumns().map((column) => (
            <th key={column.key} className="text-right" style={{ minWidth: 100 }}>
              {column.label}
            </th>
          ))}
          <th className="text-right" style={{ minWidth: 110 }}>
            ยอดรวมของบิล
          </th>
          <th className="text-right" style={{ minWidth: 130 }}>
            ผลรวมยอดชำระตามบิล
          </th>
          <th className="text-center" style={{ minWidth: 130 }}>
            สถานะกระทบยอด
          </th>
          <th className="text-right" style={{ minWidth: 100 }}>
            ยอดปัดเศษ
          </th>
        </tr>
      </thead>
    );
  }

  renderRightRow(row) {
    const height = this.rowHeight(row);
    const status = RECON_STATUS[row.status] || {};

    // ฝั่งนี้ไม่มีคู่ -> รายการเปล่า ทุกช่องเป็น 0.00 (เดิมใช้ ghost row + colSpan ทำให้ช่องว่าง)
    const items = row.right.length ? row.right : [ghostRightItem(row)];
    const isGhost = !row.right.length;

    const line = (content, index, extra) => (
      <div key={index} style={{ height: LINE_HEIGHT, lineHeight: `${LINE_HEIGHT}px` }} className={`text-truncate ${extra || ""}`}>
        {content}
      </div>
    );

    return (
      <tr key={row.key} style={{ height, borderLeft: `3px solid ${status.bar || "transparent"}` }} className={`${isGhost ? "text-muted " : ""}${this.rowClass(row)}`} onMouseEnter={() => this.hoverRow(row.key)} onClick={() => this.setState({ selected_key: row.key })}>
        <td className="align-top">
          {items.map((item, index) =>
            line(
              <>
                <span className={item.ghost ? "fst-italic" : ""}>{item.ghost ? item.placeholder : item.iw_number}</span>
                {index === 0 && items.length > 1 && <span className="badge bg-secondary ml-1">×{items.length}</span>}
              </>,
              index
            )
          )}
        </td>
        <td className="text-center align-top">{items.map((item, index) => line(item.ghost ? "-" : thaiDate(item.record_date), index))}</td>
        <td className="text-center align-top">{items.map((item, index) => line(thaiDateOrDash(item.meter_read_date), index))}</td>
        <td className="align-top">{items.map((item, index) => line(item.address_number, index))}</td>
        <td className="align-top">
          {items.map((item, index) =>
            line(
              <>
                {item.full_name}
                {index === 0 && row.name_mismatch && (
                  <span className="text-warning ml-1" title="ชื่อฝั่งใบแจ้งหนี้กับใบเสร็จไม่ตรงกัน (อาจเปลี่ยนเจ้าของ)">
                    ⓘ
                  </span>
                )}
              </>,
              index
            )
          )}
        </td>
        <td className="text-center align-top">{items.map((item, index) => line(item.ghost ? "-" : <span className="badge bg-info text-dark">บิลน้ำ</span>, index))}</td>
        <td className="text-center align-top">{items.map((item, index) => line(item.ghost ? "-" : item.payment_type_label, index))}</td>
        <td className="text-center align-top">
          {items.map((item, index) => line(item.ghost ? "-" : <span className="badge bg-light text-dark border">ปกติ</span>, index))}
        </td>
        {this.rightFieldColumns().map((column) => (
          <td key={column.key} className="text-right align-top">
            {items.map((item, index) => line(column.signed ? signedMoney(item[column.key]) : money(item[column.key]), index))}
          </td>
        ))}
        <td className="text-right align-top fw-bold">
          {items.map((item, index) => line(money(item.bill_amount), index))}
        </td>
        <td className="text-right align-top">
          {items.map((item, index) =>
            line(
              <>
                {item.total_mismatch && (
                  <span className="text-warning mr-1" title={`ยอดสุทธิที่บันทึกบนใบเสร็จ ${money(item.receipt_total_final)} ไม่เท่ากับยอดรวมรายช่อง — ใช้ยอดตามรายงานใบเสร็จรับเงิน`}>
                    ⓘ
                  </span>
                )}
                {money(item.receipt_paid_total)}
              </>,
              index
            )
          )}
        </td>
        <td className="text-center align-top">
          {line(
            <span className={`badge ${status.chip}`}>
              {status.icon} {status.label}
            </span>,
            0
          )}
        </td>
        <td className="text-right align-top">{items.map((item, index) => line(money(item.rounding_amount), index))}</td>
      </tr>
    );
  }

  // ชี้แถวไหน ให้ทั้งสองฝั่งไฮไลต์พร้อมกัน (คีย์เดียวกันทั้งซ้าย-ขวา)
  hoverRow = (key) => {
    if (this.state.hover_key !== key) this.setState({ hover_key: key });
  };

  rowClass(row) {
    const s = this.state;
    if (s.selected_key === row.key) return "gl-row-selected";
    if (s.hover_key === row.key) return "gl-row-hover";
    return "";
  }

  // เนื้อตาราง 1 ฝั่ง พร้อม spacer ของ virtual scroll
  renderPane(side, rows, window_, maxHeight) {
    const isLeft = side === "left";
    const columnCount = isLeft ? this.leftColumns().length + 1 : RIGHT_FIXED_BEFORE + this.rightFieldColumns().length + RIGHT_FIXED_AFTER;
    const slice = rows.slice(window_.start, window_.end);

    return (
      <div ref={isLeft ? this.leftScroll : this.rightScroll} onScroll={this.onScroll(side)} style={{ maxHeight, overflow: "auto" }}>
        <table className="table table-borderless table-striped mb-0 gl-table gl-fixed-head" style={{ tableLayout: "auto" }}>
          {isLeft ? this.renderLeftHead() : this.renderRightHead()}
          <tbody onMouseLeave={() => this.setState({ hover_key: null })}>
            {window_.padTop > 0 && (
              <tr style={{ height: window_.padTop }}>
                <td colSpan={columnCount} className="p-0" />
              </tr>
            )}
            {slice.map((row) => (isLeft ? this.renderLeftRow(row) : this.renderRightRow(row)))}
            {window_.padBottom > 0 && (
              <tr style={{ height: window_.padBottom }}>
                <td colSpan={columnCount} className="p-0" />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }

  renderSplit(rows) {
    const window_ = this.windowRows(rows);

    // แถบ Footer ผลรวม — เว้นระยะให้กว้างขึ้นและขยายตัวเลขให้เด่น อ่านง่ายกว่าตัวหนังสือข้างๆ
    const leftFooter = (
      <div className="border-top pt-3 pb-2 px-3 fw-bold d-flex align-items-center justify-content-between">
        <span style={{ fontSize: 14 }}>รวม {rows.reduce((acc, row) => acc + row.left.length, 0)} รายการ</span>
        <span style={{ fontSize: 20 }}>{money(sum(rows, "billed_amount"))} บาท</span>
      </div>
    );
    const rightFooter = (
      <div className="border-top pt-3 pb-2 px-3 fw-bold d-flex align-items-center justify-content-between">
        {/* ลบใบที่ยกเลิกออก (void_count) — จำนวนจึงเท่ากับรายงานใบเสร็จรับเงิน */}
        <span style={{ fontSize: 14 }}>รวม {rows.reduce((acc, row) => acc + row.receipt_count - row.void_count, 0)} รายการ</span>
        <span className="text-right">
          <span style={{ fontSize: 20 }}>{money(sum(rows, "paid_amount"))} บาท</span>
          <span className="d-block text-muted" style={{ fontSize: 13, fontWeight: 400 }}>
            ยอดปัดเศษรวม {money(sumRoundingAmount(rows))} บาท
          </span>
        </span>
      </div>
    );

    const view = this.effectiveView();
    const maxHeight = this.paneMaxHeight();
    const leftSide = (
      <>
        <div className="fw-bold mb-1">◀ ใบแจ้งหนี้ประจำเดือน (Billed)</div>
        {this.renderPane("left", rows, window_, maxHeight)}
        {leftFooter}
      </>
    );
    const rightSide = (
      <>
        <div className="fw-bold mb-1">ใบเสร็จรับเงิน — บิลน้ำ (Received) ▶</div>
        {this.renderPane("right", rows, window_, maxHeight)}
        {rightFooter}
      </>
    );

    return this.renderTablesFrame(
      view === "SPLIT" ? (
        <div className="d-flex">
          <div style={{ width: "50%", minWidth: 0 }} className="pr-1">
            {leftSide}
          </div>
          <div style={{ width: 1, background: "#dee2e6" }} />
          <div style={{ width: "50%", minWidth: 0 }} className="pl-1">
            {rightSide}
          </div>
        </div>
      ) : (
        <div>{view === "LEFT" ? leftSide : rightSide}</div>
      )
    );
  }

  // ความสูงกล่องเลื่อนของตาราง — เต็มจอให้ยืดเกือบเต็มความสูงจอ (เหลือที่ให้แถบเครื่องมือ + หัวข้อ + แถวรวม)
  paneMaxHeight = () => (this.state.fullscreen ? "calc(100vh - 190px)" : "60vh");

  // กรอบตาราง + แถบเลือกมุมมอง / ปุ่มเต็มจอ — ใช้ร่วมกันทั้งโหมดรายเดือนและรายปี
  renderTablesFrame(content) {
    const s = this.state;
    const view = this.effectiveView();
    const frameStyle = s.fullscreen
      ? { position: "fixed", inset: 0, zIndex: 1070, background: "#fff", padding: 16, overflow: "auto" }
      : {};
    const period = s.rows_mode === "YEAR" ? `ปี ${toBuddhistYear(s.year)}` : `${MONTH_OPTIONS[s.month - 1]?.label || ""} ${toBuddhistYear(s.year)}`;

    return (
      <div ref={this.tablesRef} style={frameStyle}>
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
          <div className="d-flex flex-wrap align-items-center">
            {s.fullscreen && <span className="fw-bold mr-3 text-nowrap">กระทบยอด GL · {period}</span>}
            <div className="mr-2 mb-1">
              <SegTabs
                label="มุมมองตาราง"
                value={view}
                height={31}
                small
                options={[
                  { value: "SPLIT", label: "เทียบ 2 ฝั่ง" },
                  { value: "LEFT", label: "ใบแจ้งหนี้" },
                  { value: "RIGHT", label: "ใบเสร็จ" },
                ]}
                onChange={(value) => this.setState({ view: value }, this.resetScroll)}
              />
            </div>
            {s.rows_mode === "MONTH" && (
            <div className="mr-2 mb-1">
              <SegTabs
                label="ระดับรายละเอียดคอลัมน์"
                value={this.columnPreset()}
                height={31}
                small
                options={[
                  { value: "SUMMARY", label: "สรุป" },
                  { value: "DETAIL", label: "รายละเอียด" },
                ]}
                onChange={this.setColumnPreset}
              />
            </div>
            )}
          </div>
          <button type="button" className={`btn btn-sm mb-1 ${s.fullscreen ? "btn-dark" : "btn-outline-primary"}`} onClick={this.toggleFullscreen}>
            {s.fullscreen ? "✕ ออกจากเต็มจอ (Esc)" : "⛶ ดูเต็มจอ"}
          </button>
        </div>
        {content}
      </div>
    );
  }

  // ---------- render: ตารางโหมดรายปี ----------

  renderYear(rows) {
    const s = this.state;
    const totalOf = (key) => rows.reduce((acc, row) => acc + (Number(row[key]) || 0), 0);
    const collectionRate = totalOf("billed_amount") ? (totalOf("paid_amount") * 100) / totalOf("billed_amount") : null;

    // เจาะรายเดือนได้เฉพาะเซลล์ "เดือน" — ถ้าให้ทั้งแถวกดได้ ตอนลากเลื่อนตารางแนวนอนที่ตัวเลขจะเด้งเข้ารายเดือนโดยไม่ตั้งใจ
    // ข้อความในปุ่มลากคลุมไม่ได้ (เช็ค selection ไม่ได้ผล) และ mousedown/mouseup บนปุ่มเดียวกันยิง click เสมอ
    // จึงวัดระยะเมาส์ตั้งแต่กดจนปล่อย ขยับเกิน 5px ถือว่าลาก ไม่เจาะ · คีย์บอร์ด (Enter/Space) detail = 0 ไม่มี mousedown นำหน้า ให้ผ่านเสมอ
    const monthCell = (row) => (
      <td>
        <button
          type="button"
          className="btn btn-link p-0 text-left"
          title="ดูรายละเอียดรายเดือน"
          onMouseDown={(e) => {
            this.monthPress = { x: e.clientX, y: e.clientY };
          }}
          onClick={(e) => {
            const start = this.monthPress;
            this.monthPress = null;
            if (e.detail > 0 && start && Math.hypot(e.clientX - start.x, e.clientY - start.y) > 5) return;
            this.DrillDown(row.month);
          }}
        >
          {row.month_label} {String(toBuddhistYear(row.year)).slice(-2)}
        </button>
      </td>
    );

    const view = this.effectiveView();
    const boxStyle = s.fullscreen ? { maxHeight: this.paneMaxHeight(), overflow: "auto" } : { overflowX: "auto" };

    const leftTable = (
      <>
          <div className="fw-bold mb-1">◀ ใบแจ้งหนี้ประจำเดือน (Billed)</div>
          <div style={boxStyle}>
          <table className="table table-borderless table-striped gl-fixed-head">
            <thead>
              <tr>
                <th>เดือน</th>
                <th className="text-right">จำนวนใบแจ้งหนี้</th>
                <th className="text-right">หน่วยใช้รวม</th>
                <th className="text-right">ค่าน้ำประปารวม</th>
                <th className="text-right">ค่าส่วนกลางรวม</th>
                <th className="text-right">ค่าอื่นๆ รวม</th>
                <th className="text-right">ยอดแจ้งหนี้รวม</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.month}>
                  {monthCell(row)}
                  <td className="text-right">{row.billed_count}</td>
                  <td className="text-right">{int(row.unit_used)}</td>
                  <td className="text-right">{money(row.water_bill)}</td>
                  <td className="text-right">{money(row.common_expenses)}</td>
                  <td className="text-right">{money(row.other_amount)}</td>
                  <td className="text-right fw-bold">{money(row.billed_amount)}</td>
                </tr>
              ))}
              <tr className="fw-bold table-dark">
                <td>รวมทั้งปี</td>
                <td className="text-right">{totalOf("billed_count")}</td>
                <td className="text-right">{int(totalOf("unit_used"))}</td>
                <td className="text-right">{money(totalOf("water_bill"))}</td>
                <td className="text-right">{money(totalOf("common_expenses"))}</td>
                <td className="text-right">{money(totalOf("other_amount"))}</td>
                <td className="text-right">{money(totalOf("billed_amount"))}</td>
              </tr>
            </tbody>
          </table>
          </div>
      </>
    );

    const rightTable = (
      <>
          <div className="fw-bold mb-1">ใบเสร็จรับเงิน — บิลน้ำ (Received) ▶</div>
          <div style={boxStyle}>
          <table className="table table-borderless table-striped gl-fixed-head">
            <thead>
              <tr>
                <th>เดือน</th>
                <th className="text-right">จำนวนใบเสร็จ</th>
                <th className="text-right">ยอดรับชำระรวม</th>
                <th className="text-right">ผลต่าง</th>
                <th className="text-right">% การเก็บเงิน</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.month}>
                  {monthCell(row)}
                  <td className="text-right">{row.receipt_count}</td>
                  <td className="text-right fw-bold">{money(row.paid_amount)}</td>
                  <td className={`text-right ${Math.abs(row.diff) < 0.01 ? "text-success" : row.diff < 0 ? "text-danger" : "text-primary"}`}>{money(row.diff)}</td>
                  <td className="text-right">{`${money(row.collection_rate)}%`}</td>
                </tr>
              ))}
              <tr className="fw-bold table-dark">
                <td>รวมทั้งปี</td>
                <td className="text-right">{totalOf("receipt_count")}</td>
                <td className="text-right">{money(totalOf("paid_amount"))}</td>
                <td className="text-right">{money(totalOf("diff"))}</td>
                <td className="text-right">{collectionRate === null ? "–" : `${money(collectionRate)}%`}</td>
              </tr>
            </tbody>
          </table>
          </div>
      </>
    );

    return this.renderTablesFrame(
      view === "SPLIT" ? (
        <div className="d-flex">
          <div style={{ width: "50%", minWidth: 0 }} className="pr-1">
            {leftTable}
          </div>
          <div style={{ width: 1, background: "#dee2e6" }} />
          <div style={{ width: "50%", minWidth: 0 }} className="pl-1">
            {rightTable}
          </div>
        </div>
      ) : (
        <div>{view === "LEFT" ? leftTable : rightTable}</div>
      )
    );
  }

  // ---------- render: สถานะหน้าจอ ----------

  renderBody() {
    const s = this.state;

    if (s.error) {
      return (
        <div className="alert alert-danger d-flex align-items-center justify-content-between">
          <div>
            <div className="fw-bold">เกิดข้อผิดพลาด</div>
            <div style={{ fontSize: 13 }}>{s.error} — ไม่แสดงตัวเลขบางส่วนเพื่อไม่ให้อ่านผลกระทบยอดผิด</div>
          </div>
          <button className="btn btn-outline-danger" onClick={this.Search}>
            ลองใหม่
          </button>
        </div>
      );
    }

    if (s.searching) {
      return (
        <div className="py-5 text-center text-muted">
          <div className="spinner-border text-primary mb-2" role="status" />
          <div>กำลังโหลดข้อมูลกระทบยอด...</div>
        </div>
      );
    }

    if (s.rows === null) {
      return <div className="py-5 text-center text-muted">เลือกเงื่อนไขแล้วกดค้นหา</div>;
    }

    if (s.rows_mode === "YEAR") return this.renderYear(s.rows);

    const rows = this.visibleRows();
    if (!rows.length) {
      return (
        <div className="py-5 text-center text-muted">
          <div style={{ fontSize: 40 }}>📄</div>
          <div className="mb-2">ไม่พบข้อมูลตามเงื่อนไขที่เลือก</div>
          <button className="btn btn-outline-secondary btn-sm" onClick={this.Reset}>
            ล้างเงื่อนไข
          </button>
        </div>
      );
    }

    return this.renderSplit(rows);
  }

  render() {
    if (!perm.view) {
      return (
        <div className="w-100 min-vh-100 fade-in">
          <Navbar page1="หน้าหลัก" page2="บัญชี" page3="กระทบยอด GL" />
          <div className="body">
            <div className="p-3">
              <div className="card border-0 p-5 text-center">
                <h4 className="mb-2">403</h4>
                <div className="text-muted">ไม่มีสิทธิ์เข้าถึงเมนูนี้</div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="w-100 min-vh-100 fade-in">
        <Navbar page1="หน้าหลัก" page2="บัญชี" page3="กระทบยอด GL" />
        <div className="body">
          {/* เผื่อพื้นที่ด้านล่างกันปุ่ม "กดเพื่อไปยังด้านบน" (fixed มุมขวาล่าง) ไปบังตัวเลขรวมท้ายตาราง */}
          <div className="p-3" style={{ paddingBottom: 90 }}>
            <div className="card border-0 p-3">
              <h4 className="mb-1">กระทบยอด GL</h4>
              <div className="text-muted mb-3" style={{ fontSize: 13 }}>
                เปรียบเทียบใบแจ้งหนี้ vs ใบเสร็จรับเงิน (บิลน้ำ) — หน้านี้ตรวจสอบอย่างเดียว ไม่บันทึกหรือแก้ไขข้อมูลใดๆ
              </div>

              {this.renderCards()}
              {this.renderFilterBar()}
              {this.renderBanners()}
              {this.renderBody()}
            </div>
          </div>
        </div>
        <ScrollTopButton />
      </div>
    );
  }
}

// ยอดปัดเศษรวมของแถวที่กรองอยู่ (ใบยกเลิกถูกกรองออกจาก rows แล้ว) ใช้ร่วมกับ CSV และแถบรวมท้ายตาราง
function sumRoundingAmount(rows) {
  let total = 0;
  for (const row of rows) {
    for (const item of row.right || []) {
      total += Number(item.rounding_amount) || 0;
    }
  }
  return Math.round(total * 100) / 100;
}

// รวมยอดคอลัมน์หนึ่งของ rows
function sum(rows, key) {
  return rows.reduce((acc, row) => acc + (Number(row[key]) || 0), 0);
}
